import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { ITableProps } from '../../base/Table/models/index.js';
import { ITableColumn } from '../../base/Table/models/ITableColumn.js';
import Table from '../../base/Table/Table.js';
import { TableLogic } from '../../base/Table/TableLogic.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import AreaChart from '../AreaChart/AreaChart.js';
import BarChart from '../BarChart/BarChart.js';
import { getChartDataFromTable, getNameColumn, getSeriesKey } from '../ChartBase/ChartBaseFunctions.js';
import { ISingleChart } from '../ChartBase/models/ISingleChart.js';
import LineChart from '../LineChart/LineChart.js';

import { IChartTableComboConfiguration, IChartTableComboProps, IChartTableComboState } from './models/index.js';
import ChartWrapper from './styledComponents/ChartWrapper.js';
import TableWrapper from './styledComponents/TableWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHART_TABLE_COMBO_CATEGORY_KEY, CHART_TABLE_COMBO_COMPONENT_KEY } from './ChartTableCombo.meta.js';

export class ChartTableCombo extends ComponentBase<
	IChartTableComboProps,
	IChartTableComboState,
	IChartTableComboConfiguration
> {
	categoryKey = CHART_TABLE_COMBO_CATEGORY_KEY;
	componentKey = CHART_TABLE_COMBO_COMPONENT_KEY;

	translationContext: ITranslationContextValue | undefined;

	static defaultProps: IChartTableComboProps = {
		display: 'both',
		firstComponent: 'chart',
	};

	constructor(props: IChartTableComboProps) {
		super(props);
	}
	get formattedColumns(): ITableColumn[] {
		const nameColumn = this.nameColumn;
		const { columns = [], valueFormatType, valueFormatter } = this.props;
		const response = columns.map((column) => Object.assign({}, column));
		if (valueFormatter) {
			response
				.filter((column) => column.id !== nameColumn.id)
				.forEach((column) => {
					column.format = valueFormatter;
				});
		} else if (valueFormatType) {
			response
				.filter((column) => column.id !== nameColumn.id)
				.forEach((column) => {
					column.formatType = valueFormatType;
				});
		}
		return response;
	}
	get tableLogic(): TableLogic {
		return new TableLogic(this.tableProps, this.globalConfiguration);
	}
	get tableProps(): ITableProps {
		const { tableProps = {}, rows = [] } = this.props;
		const response: ITableProps = common.commonUtils.deepMergeObjects(tableProps, {
			rows,
			columns: this.formattedColumns,
		});
		return response;
	}
	get chartProps(): ISingleChart<any> {
		const { chartProps = {} } = this.props;
		const response: ISingleChart<any> = common.commonUtils.deepMergeObjects(chartProps, {
			data: this.chartData,
			seriesProps: this.chartSeriesProps,
			elementOverrides: this.chartElementOverries,
		});
		return response;
	}
	get chartData(): object[] {
		return getChartDataFromTable(this.tableProps, this.globalConfiguration);
	}
	get chartSeriesProps(): any[] {
		const { rows = [] } = this.props;
		const response: any[] = [];
		rows.forEach((row, rowIndex) => {
			const dataKey = getSeriesKey(row, rowIndex);
			const name = this.getNameValue(row, rowIndex);
			response.push({
				dataKey,
				name,
			});
		});
		return response;
	}
	get chartElementOverries(): ISingleChart<any>['elementOverrides'] {
		const response: ISingleChart<any>['elementOverrides'] = {};
		const { valueFormatter, valueFormatType } = this.props;
		if (valueFormatter) {
			response.tooltip = {
				formatter: valueFormatter,
			};
		} else if (valueFormatType) {
			if (this.globalConfiguration && this.globalConfiguration.formats) {
				const formatter = this.globalConfiguration.formats[valueFormatType];
				if (formatter) {
					response.tooltip = {
						formatter,
					};
				}
			}
		}
		return response;
	}
	get nameColumn(): ITableColumn {
		const { columns = [] } = this.props;
		return getNameColumn(columns);
	}
	getNameValue = (row: object, index: number) => {
		const column = this.nameColumn;
		return this.tableLogic.getValue(row, column, this.translationContext, undefined, undefined, undefined, index);
	};
	get showTable(): boolean {
		const { display } = this.props;
		return display === 'both' || display === 'table';
	}
	get showChart(): boolean {
		const { display } = this.props;
		return display === 'both' || display === 'chart';
	}
	get chartComponent() {
		const { chartType = 'Bar' } = this.props;
		if (chartType === 'Area') {
			return <AreaChart {...this.chartProps} />;
		}
		if (chartType === 'Bar') {
			return <BarChart {...this.chartProps} />;
		}
		if (chartType === 'Line') {
			return <LineChart {...this.chartProps} />;
		}
		return null;
	}
	renderChart = () => {
		if (this.showChart) {
			const chartComponent = this.chartComponent;
			if (!common.commonUtils.isNullOrUndefined(chartComponent)) {
				return <ChartWrapper key="chart">{chartComponent}</ChartWrapper>;
			}
			return null;
		}
		return null;
	};
	renderTable = () => {
		if (this.showTable) {
			return (
				<TableWrapper>
					<Table {...this.tableProps} />
				</TableWrapper>
			);
		}
		return null;
	};
	render() {
		const { className, firstComponent = 'chart' } = this.props;
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext) => {
						this.translationContext = translationContext;
						return (
							<Fragment>
								{firstComponent === 'chart' && this.renderChart()}
								{this.renderTable()}
								{firstComponent === 'table' && this.renderChart()}
							</Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(ChartTableCombo),
	CHART_TABLE_COMBO_CATEGORY_KEY,
	CHART_TABLE_COMBO_COMPONENT_KEY,
);
